import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { updateCompanyProfileValue } from '@reducers/membership';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { buildSettingsNavParams, type SettingsRootStack } from '@components/settings/v2/common/navigationParams';
import type { RootState } from '@store/types';

export interface UseManageNotificationsParams {
  rootStack?: SettingsRootStack;
}

export interface UseManageNotificationsReturn {
  notifyOnMachineErrors: boolean;
  notificationLoading: boolean;
  triggerNotification: (value: boolean) => Promise<void>;
  navigateToEmailReports: () => void;
}

export const useManageNotifications = ({ rootStack = 'Settings' }: UseManageNotificationsParams = {}): UseManageNotificationsReturn => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();

  const notifyOnMachineErrors = useSelector(
    (state: RootState) => state.user?.userInfo?.companyProfile?.notifyOnMachineErrors ?? false,
  );

  const [notificationLoading, setNotificationLoading] = useState(false);

  const triggerNotification = useCallback(
    async (value: boolean) => {
      setNotificationLoading(true);
      await dispatch(updateCompanyProfileValue({ notifyOnMachineErrors: value }));
      trackEvent(TRACKING_EVENTS.SETTINGS, {
        action: 'notificationsToggled',
        notifyOnMachineErrors: value,
      });
      setNotificationLoading(false);
    },
    [dispatch],
  );

  const navigateToEmailReports = useCallback(() => {
    navigation.navigate(rootStack as never, buildSettingsNavParams(rootStack, 'Notifications', 'EmailReports') as never);
  }, [navigation, rootStack]);

  return {
    notifyOnMachineErrors,
    notificationLoading,
    triggerNotification,
    navigateToEmailReports,
  };
};
